<template>
 <div class="card">

 <!-- Top row: avatar | name + elapsed + pill — tap to expand -->
 <div class="flex items-start gap-3 cursor-pointer" @click="toggleExpand()">
 <div
 class="h-14 w-14 rounded-full flex-shrink-0 flex items-center justify-center text-white text-lg font-semibold"
 :style="{ background: avatarGradient(title) }"
 >
 {{ initials(title) || '?' }}
 </div>
 <div class="flex-1 min-w-0">
 <div class="flex justify-between items-start gap-2">
 <button
 @click.stop="canOpenContact ? openContact() : copyText(inquiry.id, 'name', title)"
 :class="['font-semibold text-fg-base dark:text-fg-base-dark text-sm leading-tight text-left focus:outline-none', canOpenContact && 'hover:underline']"
 >{{ copiedKey === `${inquiry.id}:name` ? 'Copied!' : title }}</button>
 <div class="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
 <span class="text-xs text-fg-faint dark:text-fg-faint-dark tabular-nums">
 {{ topBadgeTime(inquiry) }}
 </span>
 <svg
 :class="['h-3.5 w-3.5 text-fg-faint dark:text-fg-faint-dark transition-transform duration-200', expanded ? 'rotate-180' : '']"
 fill="none" viewBox="0 0 24 24" stroke="currentColor"
 >
 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
 </svg>
 </div>
 </div>
 <div class="mt-1">
 <template v-if="canRevert(inquiry)">
 <button
 @click.stop="handleRevert(inquiry)"
 :class="[
 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold focus:outline-none',
 revertKey === inquiry.id
 ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
 : stageInfo(inquiry).badgeClass
 ]"
 >{{ revertKey === inquiry.id ? 'Revert?' : stageInfo(inquiry).label }}</button>
 <span v-if="stageDetail(inquiry) && revertKey !== inquiry.id" class="ml-1 text-xs text-fg-muted dark:text-fg-muted-dark">{{ stageDetail(inquiry) }}</span>
 </template>
 <template v-else>
 <span :class="['inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold', stageInfo(inquiry).badgeClass]">{{ stageInfo(inquiry).label }}</span>
 <span v-if="stageDetail(inquiry)" class="ml-1 text-xs text-fg-muted dark:text-fg-muted-dark">{{ stageDetail(inquiry) }}</span>
 </template>
 <button
 v-if="hasSms(inquiry)"
 @click.stop="copyInquirySms(inquiry)"
 :disabled="smsLoading === inquiry.id"
 class="ml-1 text-xs text-indigo-500 dark:text-indigo-400 disabled:opacity-40 transition-colors focus:outline-none"
 >{{ smsCopiedId === inquiry.id ? '✓' : smsLoading === inquiry.id ? '…' : 'copy sms' }}</button>
 </div>
 <div
 v-if="inquiry.balance_badge"
 :class="['mt-1 inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold',
 inquiry.balance_badge.blocked
 ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
 : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300']"
 >{{ inquiry.balance_badge.text }}</div>
 <div v-if="inquiry.booking_data?.some(s => !s._cleared && !isSlotExpired(s))" class="mt-1 space-y-0.5">
 <template v-for="(slot, i) in inquiry.booking_data.filter(s => !s._cleared && !isSlotExpired(s))" :key="i">
 <div v-if="editSlot && editSlot.key === editSlotKey(inquiry.id, slot)" class="space-y-1 text-xs">
 <div class="flex items-center gap-1 flex-wrap">
 <input type="date" v-model="editSlot.newDate" @click.stop
 class="text-xs px-1.5 py-0.5 rounded border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-indigo-400" />
 <input type="time" v-model="editSlot.newTime" @click.stop
 class="text-xs px-1.5 py-0.5 rounded border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-indigo-400" />
 <span v-if="slot.teacher" class="text-fg-faint dark:text-fg-faint-dark">· {{ slot.teacher.split(' ')[0] }}</span>
 </div>
 <div class="flex items-center gap-2">
 <button @click.stop="editSlot = null" class="text-fg-faint dark:text-fg-faint-dark focus:outline-none">cancel</button>
 <button @click.stop="saveSlotTime(inquiry)" :disabled="actionPending"
 class="text-indigo-500 dark:text-indigo-400 disabled:opacity-40 font-medium focus:outline-none">
 {{ actionPending ? '…' : 'Save & Resend' }}
 </button>
 </div>
 </div>
 <div v-else class="flex items-center gap-1 min-w-0 overflow-hidden text-xs text-fg-muted dark:text-fg-muted-dark">
 <button @click.stop="copyText(inquiry.id, `slot_${i}`, fmtSlotCopy(slot.date, slot.time, slot.end_time))" class="flex-shrink-0 focus:outline-none">
 <template v-if="copiedKey === `${inquiry.id}:slot_${i}`"><span class="font-medium text-fg-secondary dark:text-fg-secondary-dark">Copied!</span></template>
 <template v-else><span class="font-medium text-fg-secondary dark:text-fg-secondary-dark">{{ fmtSlotDate(slot.date) }}</span><span v-if="slot.time"> · {{ fmtSlotTime(slot.time) }}</span><span v-if="slot.end_time">–{{ fmtSlotTime(slot.end_time) }}</span></template>
 </button>
 <template v-if="slot.teacher">
 <span class="flex-shrink-0"> · </span>
 <a v-if="isAdmin && teacherPhone(slot.teacher)" :href="`sms:${formatE164(teacherPhone(slot.teacher))}`" @click.stop class="truncate min-w-0 focus:outline-none">{{ slot.teacher }}</a>
 <button v-else-if="teacherPhone(slot.teacher)" @click.stop="copyText(inquiry.id, `teacher_${i}`, teacherPhone(slot.teacher))" class="truncate min-w-0 focus:outline-none">{{ copiedKey === `${inquiry.id}:teacher_${i}` ? 'Copied!' : slot.teacher }}</button>
 <span v-else class="truncate min-w-0">{{ slot.teacher }}</span>
 <button @click.stop="startEditSlot(inquiry, slot)"
 class="flex-shrink-0 ml-1 text-fg-faint dark:text-fg-faint-dark transition-colors focus:outline-none">edit</button>
 </template>
 </div>
 </template>
 </div>
 </div>
 </div>

 <!-- Collapsed contact bar + action buttons -->
 <div v-if="!expanded" class="mt-2.5 flex flex-col gap-2">
 <!-- Contact info -->
 <div class="flex items-center justify-left gap-3 min-w-0 overflow-hidden">
 <button
 v-if="contactName"
 @click.stop="copyText(inquiry.id, 'contact', contactName)"
 class="text-xs text-fg-muted dark:text-fg-muted-dark truncate text-left focus:outline-none"
 >{{ copiedKey === `${inquiry.id}:contact` ? 'Copied!' : contactName }}</button>
 <button
 v-if="inquiry.phone"
 @click.stop="copyText(inquiry.id, 'phone', formatPhone(inquiry.phone))"
 class="text-xs text-fg-muted dark:text-fg-muted-dark tabular-nums text-left whitespace-nowrap shrink-0"
 :title="inquiry.phone"
 >{{ copiedKey === `${inquiry.id}:phone` ? 'Copied!' : formatPhone(inquiry.phone) }}</button>
 <a
 v-if="inquiry.email"
 :href="`mailto:${inquiry.email}`"
 @click.stop
 class="text-xs text-fg-muted dark:text-fg-muted-dark truncate"
 >{{ inquiry.email }}</a>
 </div>

 <!-- Action buttons -->
 <div class="flex items-center gap-1.5 self-end flex-wrap justify-end">
 <!-- Waitlist / Lost / Follow Up: a dedicated X, not buried in a generic ··· menu.
 Always rendered so Follow Up is settable from any card — Waitlist/Lost
 stay restricted to stages that support them. Check-in cards use Check In instead. -->
 <button v-if="isLost(inquiry)" @click.stop="startReopen(inquiry)" :disabled="actionPending" class="action-btn-ghost">Reopen</button>
 <div v-if="cardAction(inquiry) !== 'checkin' && !(isLost(inquiry) && !cardAction(inquiry))" class="relative inline-flex">
 <button @click.stop="toggleDropdown('x_' + inquiry.id)" :disabled="actionPending" class="quick-log-btn" title="Waitlist / Lost / Follow Up">
 <XMarkIcon class="h-4 w-4" />
 </button>
 <div v-if="openDropdownId === 'x_' + inquiry.id" @click.stop class="actions-dropdown">
 <template v-if="['mark_called','approve_slot','waitlist_request_time','lost_waitlist'].includes(cardAction(inquiry))">
 <button @click="waitlistInquiry(inquiry); closeDropdown()" :disabled="actionPending" class="dropdown-item">Waitlist</button>
 <button @click="markLost(inquiry); closeDropdown()" :disabled="actionPending" class="dropdown-item">Lost</button>
 </template>
 <button @click="scheduleFollowUp(inquiry); closeDropdown()" :disabled="actionPending" class="dropdown-item">Follow Up</button>
 </div>
 </div>

 <!-- Call / Add Note (note, outside text, outside email) — any stage: a family
 can be called or texted at any point in a signup's life. -->
 <RouterLink v-if="canText" :to="{ name: 'AdminSmsInbox', query: { phone: inquiry.phone } }" @click.stop class="quick-log-btn" title="Send a text">
 <ChatBubbleLeftEllipsisIcon class="h-4 w-4" />
 </RouterLink>
 <LogTouchButtons :disabled="actionPending" @log="startLog(inquiry, $event)" />

 <!-- Remaining actions that don't fit call/text/email/other or waitlist/lost -->
 <template v-if="cardAction(inquiry) === 'approve_slot'">
 <button @click.stop="toggleRequestTime()" :disabled="actionPending" class="action-btn-ghost">{{ rtOpen ? 'Cancel' : 'Request Time' }}</button>
 <button @click.stop="sendInviteFromApproval(inquiry)" :disabled="actionPending" class="action-btn-primary">{{ actionPending ? '…' : 'Send Invite' }}</button>
 </template>
 <template v-else-if="cardAction(inquiry) === 'waitlist_request_time'">
 <button @click.stop="toggleRequestTime()" :disabled="actionPending" class="action-btn-primary">{{ rtOpen ? 'Cancel' : 'Request Time' }}</button>
 </template>
 <button v-else-if="cardAction(inquiry) === 'checkin'" @click.stop="startCheckin(inquiry)" :disabled="actionPending" class="action-btn-primary">Check In</button>
 </div>
 </div>

 <!-- Compact fields: student, instrument, contact, preferred times -->
 <dl v-if="expanded" class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
 <div v-if="contactName">
 <dt class="text-xs font-medium text-fg-faint dark:text-fg-faint-dark uppercase tracking-wide">Contact</dt>
 <dd class="mt-0.5">
 <button
 @click.stop="copyText(inquiry.id, 'contact', contactName)"
 class="text-fg-base dark:text-fg-base-dark text-left focus:outline-none"
 >{{ copiedKey === `${inquiry.id}:contact` ? 'Copied!' : contactName }}</button>
 </dd>
 </div>
 <div v-if="inquiry.instrument">
 <dt class="text-xs font-medium text-fg-faint dark:text-fg-faint-dark uppercase tracking-wide">Instrument</dt>
 <dd class="text-fg-base dark:text-fg-base-dark mt-0.5">{{ inquiry.instrument }}</dd>
 </div>
 <div v-if="inquiry.phone">
 <dt class="text-xs font-medium text-fg-faint dark:text-fg-faint-dark uppercase tracking-wide">Phone</dt>
 <dd class="mt-0.5">
 <button
 @click.stop="copyText(inquiry.id, 'phone', formatPhone(inquiry.phone))"
 class="text-fg-base dark:text-fg-base-dark tabular-nums text-left focus:outline-none"
 >{{ copiedKey === `${inquiry.id}:phone` ? 'Copied!' : formatPhone(inquiry.phone) }}</button>
 </dd>
 </div>
 <div v-if="inquiry.email">
 <dt class="text-xs font-medium text-fg-faint dark:text-fg-faint-dark uppercase tracking-wide">Email</dt>
 <dd class="mt-0.5">
 <a
 :href="`mailto:${inquiry.email}`"
 @click.stop
 class="text-fg-base dark:text-fg-base-dark truncate block"
 >{{ inquiry.email }}</a>
 </dd>
 </div>
 <div v-if="inquiry.preferred_times" class="col-span-2">
 <dt class="text-xs font-medium text-fg-faint dark:text-fg-faint-dark uppercase tracking-wide">Preferred Times</dt>
 <dd class="text-fg-secondary dark:text-fg-secondary-dark mt-0.5">{{ inquiry.preferred_times }}</dd>
 </div>
 </dl>

 <!-- Extended fields: experience, musical taste (message is in History) -->
 <div
 v-if="expanded && (inquiry.experience || inquiry.musical_taste)"
 class="mt-3 pt-3 border-t border-stroke-subtle dark:border-stroke-dark space-y-2.5"
 >
 <div v-if="inquiry.experience">
 <p class="text-xs font-medium text-fg-faint dark:text-fg-faint-dark uppercase tracking-wide">Experience</p>
 <p class="text-sm text-fg-secondary dark:text-fg-secondary-dark mt-0.5">{{ inquiry.experience }}</p>
 </div>
 <div v-if="inquiry.musical_taste">
 <p class="text-xs font-medium text-fg-faint dark:text-fg-faint-dark uppercase tracking-wide">Musical Taste</p>
 <p class="text-sm text-fg-secondary dark:text-fg-secondary-dark mt-0.5">{{ inquiry.musical_taste }}</p>
 </div>
 </div>

 <!-- History timeline -->
 <div v-if="expanded" class="mt-3 pt-3 border-t border-stroke-subtle dark:border-stroke-dark">
 <ActivityTimeline
 title="History"
 :events="history || []"
 :loading="historyLoading"
 empty-text="No history yet"
 :limit="30"
 />
 </div>

 <!-- Action buttons (expanded state): quick-log row always available, plus
 whatever stage-specific action doesn't fit call/text/email/other or
 waitlist/lost (see the compact view above for the same layout/rationale). -->
 <div v-if="expanded" class="mt-3 pt-3 border-t border-stroke-subtle dark:border-stroke-dark flex items-center justify-end gap-1.5 flex-wrap">
 <button v-if="isLost(inquiry)" @click.stop="startReopen(inquiry)" :disabled="actionPending" class="action-btn-ghost">Reopen</button>
 <div v-if="cardAction(inquiry) !== 'checkin' && !(isLost(inquiry) && !cardAction(inquiry))" class="relative inline-flex">
 <button @click.stop="toggleDropdown('ex_' + inquiry.id)" :disabled="actionPending" class="quick-log-btn" title="Waitlist / Lost / Follow Up">
 <XMarkIcon class="h-4 w-4" />
 </button>
 <div v-if="openDropdownId === 'ex_' + inquiry.id" @click.stop class="actions-dropdown">
 <template v-if="['mark_called','approve_slot','waitlist_request_time','lost_waitlist'].includes(cardAction(inquiry))">
 <button @click="waitlistInquiry(inquiry); closeDropdown()" :disabled="actionPending" class="dropdown-item">Waitlist</button>
 <button @click="markLost(inquiry); closeDropdown()" :disabled="actionPending" class="dropdown-item">Lost</button>
 </template>
 <button @click="scheduleFollowUp(inquiry); closeDropdown()" :disabled="actionPending" class="dropdown-item">Follow Up</button>
 </div>
 </div>

 <RouterLink v-if="canText" :to="{ name: 'AdminSmsInbox', query: { phone: inquiry.phone } }" @click.stop class="quick-log-btn" title="Send a text">
 <ChatBubbleLeftEllipsisIcon class="h-4 w-4" />
 </RouterLink>
 <LogTouchButtons :disabled="actionPending" @log="startLog(inquiry, $event)" />

 <button
 v-if="canClearSlot(inquiry)"
 @click.stop="handleClearSlot(inquiry)"
 :disabled="actionPending"
 :class="clearSlotKey === inquiry.id ? 'action-btn-danger' : 'action-btn-ghost'"
 >{{ clearSlotKey === inquiry.id ? 'Clear?' : 'Clear Slot' }}</button>

 <template v-if="cardAction(inquiry) === 'approve_slot'">
 <button @click.stop="toggleRequestTime()" :disabled="actionPending" class="action-btn-ghost">{{ rtOpen ? 'Cancel' : 'Request Time' }}</button>
 <button @click.stop="sendInviteFromApproval(inquiry)" :disabled="actionPending" class="action-btn-primary">{{ actionPending ? '…' : 'Send Invite' }}</button>
 </template>
 <template v-else-if="cardAction(inquiry) === 'waitlist_request_time'">
 <button @click.stop="toggleRequestTime()" :disabled="actionPending" class="action-btn-primary">{{ rtOpen ? 'Cancel' : 'Request Time' }}</button>
 </template>
 <button v-else-if="cardAction(inquiry) === 'checkin'" @click.stop="startCheckin(inquiry)" :disabled="actionPending" class="action-btn-primary">Check In</button>
 </div>

 <!-- Inline Request Time slot picker (admin only) -->
 <div v-if="rtOpen" class="mt-3 pt-3 border-t border-stroke-subtle dark:border-stroke-dark space-y-2">

 <!-- Teacher dropdown -->
 <select
 v-model="rtTeacher"
 class="signup-select"
 >
 <option :value="null" disabled>Select teacher…</option>
 <option v-for="t in rtTeachers" :key="t.id" :value="t">{{ t.name }}</option>
 </select>

 <template v-if="rtTeacher">
 <!-- Week nav -->
 <div class="flex items-center justify-between text-xs text-fg-muted dark:text-fg-muted-dark">
 <button @click.stop="rtWeekOff--" class="px-2 py-0.5 rounded">←</button>
 <span class="font-medium">{{ rtWeekLabel }}</span>
 <button @click.stop="rtWeekOff++" class="px-2 py-0.5 rounded">→</button>
 </div>

 <!-- Day rows -->
 <div class="space-y-2">
 <template v-for="day in rtDays" :key="day.dateStr">
 <div v-if="day.slots.length > 0">
 <div class="text-xs font-medium text-fg-faint dark:text-fg-faint-dark mb-1">{{ day.dayLabel }}</div>
 <div class="flex flex-wrap gap-1">
 <button
 v-for="slot in day.slots"
 :key="slot.time"
 :disabled="slot.timeOff"
 :title="slot.timeOff ? 'Teacher unavailable' : null"
 @click.stop="rtToggleSlot(day.dateStr, slot.time, slot.timeOff)"
 :class="slot.timeOff ? 'slot-chip-timeoff' : rtIsPending(day.dateStr, slot.time) ? 'slot-chip-selected' : slot.occupied ? 'slot-chip-occupied' : 'slot-chip'"
 >{{ slot.label }}</button>
 </div>
 </div>
 </template>
 </div>

 <!-- Custom time (outside teacher's normal availability) -->
 <div v-if="!rtCustomSlot" class="flex items-center gap-1 flex-wrap">
 <span class="text-xs text-fg-faint dark:text-fg-faint-dark">Other:</span>
 <input type="date" v-model="rtCustomDate" @click.stop
 class="text-xs px-1.5 py-0.5 rounded border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-indigo-400" />
 <input type="time" step="900" v-model="rtCustomTime" @click.stop
 class="text-xs px-1.5 py-0.5 rounded border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-indigo-400" />
 <button @click.stop="rtAddCustomSlot" :disabled="!rtCustomDate || !rtCustomTime" class="action-btn-ghost px-2 py-0.5 disabled:opacity-40">Add</button>
 </div>

 <!-- Pending slots + send button(s) -->
 <div v-if="rtPending.length" class="space-y-2 pt-1 border-t border-stroke-subtle dark:border-stroke-dark">
 <div class="flex flex-wrap gap-1">
 <span
 v-for="s in rtPending"
 :key="s.date + s.time"
 class="slot-tag"
 >
 {{ fmtSlotDate(s.date) }} · {{ fmtSlotTime(s.time) }}
 <button @click.stop="rtRemoveSlot(s)" class="text-primary-400 leading-none">×</button>
 </span>
 </div>
 <div class="flex gap-2">
 <button
 v-if="rtPending.length === 1"
 @click.stop="rtSendInviteDirect(inquiry)"
 :disabled="actionPending"
 class="action-btn-ghost flex-1 py-1.5 transition-colors"
 >{{ actionPending ? '…' : 'Skip teacher · Send invite' }}</button>
 <button
 @click.stop="rtSendSlots(inquiry)"
 :disabled="actionPending"
 class="action-btn-primary flex-1 py-1.5 transition-colors"
 >{{ actionPending ? 'Sending…' : `Send ${rtPending.length} slot${rtPending.length !== 1 ? 's' : ''} to ${rtTeacher.name.split(' ')[0]}` }}</button>
 </div>
 </div>
 </template>
 </div>

  <Teleport to="body">
 <NotePromptModal
 v-if="notePrompt"
 :title="notePromptTitle"
 :subtitle="notePromptSubtitle"
 :saving="notePromptSaving"
 :note-required="notePromptNoteRequired"
 :show-timestamp="notePromptShowTimestamp"
 :save-label="notePromptSaveLabel"
 :show-checkin-outcome="notePromptShowCheckinOutcome"
 :show-date-picker="notePromptShowDatePicker"
 :reopen-modes="notePromptReopenModes"
 @save="saveNotePrompt"
 @close="closeNotePrompt"
 />
 <WaitlistEntryForm
 v-if="waitlistFor"
 :entry="{ student_name: waitlistFor.student_name || waitlistFor.name, instrument: waitlistFor.instrument }"
 :title="`Waitlist: ${waitlistFor.student_name || waitlistFor.name}`"
 :on-submit="submitWaitlist"
 @close="waitlistFor = null"
 @saved="waitlistFor = null"
 />
 </Teleport>
 </div>
</template>

<script>
// Shared across every SignupCard instance: only one ··· dropdown open page-wide,
// and teacher phones are fetched once per page load.
import { ref as _ref } from 'vue'
const openDropdownId = _ref(null)
const teacherPhones = _ref({})
let teacherPhonesLoaded = false
</script>

<script setup>
// One lesson-signup card — used by the dashboard's LessonSignupsTab and by
// ContactPanel's Inquiries section. Owns every per-card action; the parent
// just swaps in the row this emits as `updated`.
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import {
 XMarkIcon, ChatBubbleLeftEllipsisIcon,
} from '@heroicons/vue/24/outline'
import { useRoute, useRouter } from 'vue-router'
import { useApi } from '../../composables/useApi.js'
import { useAdminSidebar } from '../../composables/useAdminSidebar.js'
import { stageInfo, cardAction } from '../../composables/useSubmissionStage.js'
import { avatarGradient, initials } from '../../composables/useAvatarGradient.js'
import { useNowTicker, elapsedTime, agoLabel, fmtSlotDate, fmtSlotTime, formatPhone } from '../../composables/useSignupFormat.js'
import NotePromptModal from './NotePromptModal.vue'
import WaitlistEntryForm from './WaitlistEntryForm.vue'
import ActivityTimeline from './ActivityTimeline.vue'
import LogTouchButtons from './LogTouchButtons.vue'

const props = defineProps({
 inquiry:         { type: Object, required: true },
 isAdmin:         { type: Boolean, default: false },
})

// Same Send Text shortcut as ContactPanel — hidden when already on the SMS inbox.
const route = useRoute()
const canText = computed(() => !!(props.inquiry.phone && props.inquiry.sms_consent) && route.name !== 'AdminSmsInbox')
const emit = defineEmits(['updated', 'busy'])

// The title opens the family's contact panel (sidebar, else the directory) — this card is
// only shown to admin/lesson_signups staff. Inside that contact's own panel it copies the name.
const router = useRouter()
const adminSidebar = useAdminSidebar()
const canOpenContact = computed(() => !!props.inquiry.contact_id && adminSidebar.openContactId.value !== props.inquiry.contact_id)
function openContact() {
  const contactId = props.inquiry.contact_id
  if (adminSidebar.available.value) adminSidebar.open('contact', { contactId })
  else router.push(`/admin/contacts/directory?contact=${contactId}`)
}

const { fetchApi, logInteraction } = useApi()
const now = useNowTicker()

// Card is titled by the student; the contact (usually a parent) is secondary
// and only shown when it's a different person.
const title = computed(() => props.inquiry.student_name || props.inquiry.name)
const contactName = computed(() => props.inquiry.student_name && props.inquiry.name !== props.inquiry.student_name ? props.inquiry.name : null)

const copiedKey = ref(null)
const smsCopiedId = ref(null)
const smsLoading = ref(null)
const revertKey = ref(null)
const clearSlotKey = ref(null)
const actionPending = ref(false)
const editSlot = ref(null) // { key, inquiryId, teacher, oldDate, oldTime, newDate, newTime, newEndTime }
let copiedTimer = null
let smsTimer = null
let revertTimer = null
let clearSlotTimer = null

// Parent pauses its poll while any card has a request in flight
watch(actionPending, v => emit('busy', v))

const toggleDropdown = (id) => { openDropdownId.value = openDropdownId.value === id ? null : id }
const closeDropdown = () => { openDropdownId.value = null }

// ── Expand + history timeline ──────────────────────────────────────────────────
const expanded = ref(false)
const history = ref(null)
const historyLoading = ref(false)

// "Loading…" only on the first fetch; refetches (polls, after an action) swap the
// list in place and only when it actually changed, so History never blanks out.
const fetchHistory = async () => {
 if (!history.value) historyLoading.value = true
 try {
 const fresh = await fetchApi(`/api/admin/submissions/${props.inquiry.id}/history`)
 if (JSON.stringify(fresh) !== JSON.stringify(history.value)) history.value = fresh
 } catch (e) {
 console.error('Failed to fetch signup history', e)
 } finally {
 historyLoading.value = false
 }
}

function toggleExpand() {
 closeDropdown()
 expanded.value = !expanded.value
 if (expanded.value) fetchHistory()
}

const invalidateHistory = () => {
 if (expanded.value) fetchHistory()
}
// A parent swapping in a changed row (e.g. ContactPanel's Log Call marking this
// signup called) means history may have changed underneath us.
watch(() => props.inquiry, invalidateHistory)
// Texts/emails/calls can land without the row changing, so poll while open.
let historyPoll = null
watch(expanded, open => {
 clearInterval(historyPoll)
 historyPoll = open ? setInterval(() => { if (!document.hidden) fetchHistory() }, 10_000) : null
})
onUnmounted(() => clearInterval(historyPoll))

// ── Top-badge time: elapsed-since-signup before contact, or countdown to the
// selected slot once called + a slot is active (far more useful at that point) —
// once that slot time has passed, falls back to elapsed-since-slot ("ago") rather
// than getting stuck on "now" forever ──────────────────────────────────────────
const topBadgeTime = (inquiry) => {
 const slot = inquiry.called_at ? (inquiry.booking_data || []).find(s => !s._cleared && s.date) : null
 if (!slot) return elapsedTime(inquiry.created_at)
 const [y, mo, d] = slot.date.split('-').map(Number)
 const [h, m] = (slot.time || '00:00').split(':').map(Number)
 const diffMs = new Date(y, mo - 1, d, h, m).getTime() - now.value
 if (diffMs <= 0) return agoLabel(-diffMs)
 const totalMins = Math.floor(diffMs / 60000)
 const hrs = Math.floor(totalMins / 60)
 const days = Math.floor(hrs / 24)
 if (days >= 1) return `in ${days}d`
 if (hrs >= 1) return `in ${hrs}hrs`
 return `in ${totalMins}m`
}

// ── Stage detail (with live countdown for "Lesson Today") ─────────────────────
const stageDetail = (inquiry) => {
 if (inquiry.stage_detail !== null && inquiry.stage_detail !== undefined) return inquiry.stage_detail
 if (inquiry.stage !== 'Lesson Today') return null
 const slot = (inquiry.booking_data || []).filter(s => !s._cleared)[0]
 if (!slot?.date) return null
 const [y, mo, d] = slot.date.split('-').map(Number)
 const [h, m] = (slot.time || '00:00').split(':').map(Number)
 const diffMs = new Date(y, mo - 1, d, h, m).getTime() - now.value
 if (diffMs <= 0) return 'now'
 const totalSecs = Math.floor(diffMs / 1000)
 const mins = Math.floor(totalSecs / 60) % 60
 const hrs = Math.floor(totalSecs / 3600)
 if (hrs >= 1) return `in ${hrs}h ${mins}m`
 if (mins >= 1) return `in ${mins}m ${totalSecs % 60}s`
 return `in ${totalSecs}s`
}

// ── Teacher phone lookup (accent/case-insensitive) ─────────────────────────────
const fmtSlotCopy = (dateStr, timeStr, endTimeStr) => {
 let text = fmtSlotDate(dateStr)
 if (timeStr) {
 text += ` • ${fmtSlotTime(timeStr)}`
 if (endTimeStr) text += `–${fmtSlotTime(endTimeStr)}`
 }
 return text
}

const formatE164 = (phone) => {
 const digits = phone.replace(/\D/g, '')
 return digits.length === 10 ? `+1${digits}` : `+${digits}`
}

const normalizeName = (s) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim()
const teacherPhone = (name) => name ? teacherPhones.value[normalizeName(name)] : null

// ── Copy helpers ───────────────────────────────────────────────────────────────
const copyText = async (id, field, text) => {
 try {
 await navigator.clipboard.writeText(text)
 copiedKey.value = `${id}:${field}`
 if (copiedTimer) clearTimeout(copiedTimer)
 copiedTimer = setTimeout(() => { copiedKey.value = null }, 1000)
 } catch {}
}

const hasSms = (inquiry) =>
 ['Waiting on Student', 'Waiting on Teacher', 'Check-in with Student'].includes(stageInfo(inquiry)?.label) ||
 stageInfo(inquiry)?.detail === 'Teacher unresponsive' ||
 (stageInfo(inquiry)?.label || '').startsWith('Unregistered')

const copyInquirySms = async (inquiry) => {
 if (stageInfo(inquiry)?.label === 'Check-in with Student') {
 const firstName = (inquiry.name || '').split(' ')[0] || 'there'
 try {
 await navigator.clipboard.writeText(`Hi, ${firstName}! How did the lesson go?`)
 smsCopiedId.value = inquiry.id
 if (smsTimer) clearTimeout(smsTimer)
 smsTimer = setTimeout(() => { smsCopiedId.value = null }, 1500)
 } catch {}
 return
 }
 if (smsLoading.value === inquiry.id) return
 smsLoading.value = inquiry.id
 try {
 const data = await fetchApi(`/api/admin/submissions/${inquiry.id}/sms-text`)
 await navigator.clipboard.writeText(data.text)
 smsCopiedId.value = inquiry.id
 if (smsTimer) clearTimeout(smsTimer)
 smsTimer = setTimeout(() => { smsCopiedId.value = null }, 1500)
 } catch (e) {
 console.error('Failed to copy SMS text', e)
 } finally {
 smsLoading.value = null
 }
}

// A slot whose date has passed — should already have been cleared by the expiry
// jobs (scheduler.py), so this is only a display guard for the top-of-card slot row.
const isSlotExpired = (slot) => {
 if (!slot?.date) return false
 const now = new Date()
 const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
 return slot.date < todayStr
}

// ── Edit Slot Time ─────────────────────────────────────────────────────────────
const editSlotKey = (inquiryId, slot) =>
 `${inquiryId}:${slot.teacher}:${slot.date}:${slot.time}`

const startEditSlot = (inquiry, slot) => {
 const time = slot.time || ''
 editSlot.value = {
 key: `${inquiry.id}:${slot.teacher}:${slot.date}:${time}`,
 inquiryId: inquiry.id,
 teacher: slot.teacher,
 oldDate: slot.date,
 oldTime: time,
 newDate: slot.date,
 newTime: time,
 newEndTime: slot.end_time || '',
 }
}

const saveSlotTime = async (inquiry) => {
 if (!editSlot.value || actionPending.value) return
 actionPending.value = true
 try {
 const updated = await fetchApi(`/api/admin/submissions/${inquiry.id}/update-slot-time`, {
 method: 'PATCH',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 teacher_name: editSlot.value.teacher,
 old_date: editSlot.value.oldDate,
 old_time: editSlot.value.oldTime,
 new_date: editSlot.value.newDate,
 new_time: editSlot.value.newTime,
 new_end_time: editSlot.value.newEndTime || null,
 }),
 })
 emit('updated', updated)
 editSlot.value = null
 } catch (e) {
 console.error('Failed to update slot time', e)
 } finally {
 actionPending.value = false
 }
}

// ── Clear Slot ─────────────────────────────────────────────────────────────────
const canClearSlot = (inquiry) =>
 (inquiry.booking_data || []).some(s => !s._cleared)

const handleClearSlot = (inquiry) => {
 if (clearSlotKey.value === inquiry.id) {
 clearSlot(inquiry)
 return
 }
 clearSlotKey.value = inquiry.id
 if (clearSlotTimer) clearTimeout(clearSlotTimer)
 clearSlotTimer = setTimeout(() => { clearSlotKey.value = null }, 3000)
}

const clearSlot = async (inquiry) => {
 if (clearSlotTimer) clearTimeout(clearSlotTimer)
 clearSlotKey.value = null
 if (actionPending.value) return
 actionPending.value = true
 try {
 const updated = await fetchApi(`/api/admin/submissions/${inquiry.id}/clear-slot`, { method: 'PATCH' })
 emit('updated', updated)
 invalidateHistory()
 closeDropdown()
 } catch (e) {
 console.error('Failed to clear slot', e)
 } finally {
 actionPending.value = false
 }
}

// ── Revert user-advanced stage ─────────────────────────────────────────────────
const isUserAdvanced = (inquiry) => {
 if (!inquiry.series_id && ['waitlist', 'confirmed', 'declined', 'cancelled'].includes(inquiry.status)) return true
 if (inquiry.series_checkin_status != null) return true
 return false
}

const canRevert = (inquiry) =>
 isUserAdvanced(inquiry) && !inquiry.series_has_billed_lessons

const handleRevert = (inquiry) => {
 if (revertKey.value === inquiry.id) {
 revertSubmission(inquiry)
 return
 }
 revertKey.value = inquiry.id
 if (revertTimer) clearTimeout(revertTimer)
 revertTimer = setTimeout(() => { revertKey.value = null }, 3000)
}

const revertSubmission = async (inquiry) => {
 if (revertTimer) clearTimeout(revertTimer)
 revertKey.value = null
 if (actionPending.value) return
 actionPending.value = true
 try {
 const updated = await fetchApi(`/api/admin/submissions/${inquiry.id}/revert`, { method: 'PATCH' })
 emit('updated', updated)
 } catch (e) {
 console.error('Failed to revert submission', e)
 } finally {
 actionPending.value = false
 }
}

// ── Waitlist action ────────────────────────────────────────────────────────────
// Opens the waitlist form (plan 64): what would work, so the family is offered openings that fit.
const waitlistFor = ref(null)
const waitlistInquiry = (inquiry) => { waitlistFor.value = inquiry }
const submitWaitlist = async (params) => {
 const inquiry = waitlistFor.value
 actionPending.value = true
 try {
 const updated = await fetchApi(`/api/admin/submissions/${inquiry.id}/waitlist`, { method: 'PATCH', body: JSON.stringify(params) })
 emit('updated', updated)
 invalidateHistory()
 } finally {
 actionPending.value = false
 }
}

const markLost = async (inquiry) => {
 if (actionPending.value) return
 actionPending.value = true
 try {
 const updated = await fetchApi(`/api/admin/submissions/${inquiry.id}/mark-lost`, { method: 'PATCH' })
 emit('updated', updated)
 invalidateHistory()
 notePrompt.value = { kind: 'lost', inquiry }
 } catch (e) {
 console.error('Failed to mark submission lost', e)
 } finally {
 actionPending.value = false
 }
}

// ── Reopen a lost signup ───────────────────────────────────────────────────────
// Marked lost, or a check-in resolved as lost. Its card shows Reopen instead of the
// X — except when the family got back in touch and it came back as a due Follow Up
// (utils/lost_signups.py), where the X stays so it can be marked Lost again.
const isLost = (inquiry) => inquiry.status === 'lost' || inquiry.series_checkin_status === 'lost'

const startReopen = (inquiry) => {
 closeDropdown()
 notePrompt.value = { kind: 'reopen', inquiry }
}

// ── Request Time (inline slot picker) ─────────────────────────────────────────
const _WEEKDAYS_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const _rtFmtDate = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`

const rtOpen = ref(false)
const rtTeachers = ref([])
const rtTeacher = ref(null)
const rtWeekOff = ref(0)
const rtPending = ref([])
const rtLessons = ref([])
const rtCancellations = ref([])  // marked time-off windows for rtTeacher this week
const rtCustomDate = ref('')
const rtCustomTime = ref('')
const rtCustomSlot = ref(null)

const rtWeekStart = computed(() => {
 const today = new Date()
 const jsDay = today.getDay()
 const toMonday = jsDay === 0 ? -6 : 1 - jsDay
 const d = new Date(today)
 d.setDate(today.getDate() + toMonday + rtWeekOff.value * 7)
 d.setHours(0, 0, 0, 0)
 return d
})

const rtWeekLabel = computed(() => {
 const start = rtWeekStart.value
 const end = new Date(start); end.setDate(end.getDate() + 6)
 const o = { month: 'short', day: 'numeric' }
 return `${start.toLocaleDateString('en-US', o)} – ${end.toLocaleDateString('en-US', o)}`
})

function _rtIsOccupied(slotMins, dayLessons) {
 return dayLessons.some(l => {
 const [h, m] = (l.start_time || '').split(':').map(Number)
 if (isNaN(h)) return false
 const startMins = h * 60 + m
 return startMins < slotMins + 30 && startMins + (l.duration_minutes || 30) > slotMins
 })
}

function _rtIsTimeOff(dateStr, slotMins) {
 const slotStart = new Date(`${dateStr}T00:00:00`); slotStart.setMinutes(slotMins)
 const slotEnd = new Date(slotStart); slotEnd.setMinutes(slotStart.getMinutes() + 30)
 return rtCancellations.value.some(c => new Date(c.start_datetime) < slotEnd && new Date(c.end_datetime) > slotStart)
}

const rtDays = computed(() => {
 if (!rtTeacher.value) return []
 const avail = rtTeacher.value.availability || {}
 const days = []
 const now = new Date()
 const todayStr = _rtFmtDate(now)
 const nowMins = now.getHours() * 60 + now.getMinutes()
 for (let i = 0; i < 7; i++) {
 const d = new Date(rtWeekStart.value); d.setDate(d.getDate() + i)
 const dateStr = _rtFmtDate(d)
 if (dateStr < todayStr) continue
 const jsDay = d.getDay()
 const dayName = _WEEKDAYS_EN[jsDay === 0 ? 6 : jsDay - 1]
 const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
 const windows = avail[dayName] || []
 const slots = []
 const dayLessons = rtLessons.value.filter(l => l.date === dateStr)
 for (const win of windows) {
 if (!win?.start || !win?.end) continue
 const [sh, sm] = win.start.split(':').map(Number)
 const [eh, em] = win.end.split(':').map(Number)
 const startMins = Math.ceil((sh * 60 + sm) / 30) * 30
 const endMins = eh * 60 + em
 for (let m = startMins; m < endMins; m += 30) {
 if (dateStr === todayStr && m <= nowMins) continue
 const hh = Math.floor(m / 60); const mm = m % 60
 const timeStr = `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}`
 slots.push({ time: timeStr, label: fmtSlotTime(timeStr), occupied: _rtIsOccupied(m, dayLessons), timeOff: _rtIsTimeOff(dateStr, m) })
 }
 }
 days.push({ dateStr, dayLabel, slots })
 }
 return days
})

async function rtFetchLessons() {
 if (!rtTeacher.value) { rtLessons.value = []; rtCancellations.value = []; return }
 const start = rtWeekStart.value
 const end = new Date(start); end.setDate(end.getDate() + 6)
 const startStr = _rtFmtDate(start)
 const endStr = _rtFmtDate(end)
 const [lessonData, cancellationData] = await Promise.all([
 fetchApi(`/api/admin/submissions/teacher-lessons?teacher_name=${encodeURIComponent(rtTeacher.value.name)}&start_date=${startStr}&end_date=${endStr}`).catch(() => []),
 fetchApi(`/api/teacher-cancellations/range?start_date=${startStr}T00:00:00&end_date=${endStr}T23:59:59&teacher_name=${encodeURIComponent(rtTeacher.value.name)}`).catch(() => []),
 ])
 rtLessons.value = lessonData
 rtCancellations.value = cancellationData
}

watch([rtTeacher, rtWeekOff], rtFetchLessons)

const rtIsPending = (dateStr, time) => rtPending.value.some(s => s.date === dateStr && s.time === time)
const rtToggleSlot = (dateStr, time, timeOff) => {
 if (timeOff) return
 const idx = rtPending.value.findIndex(s => s.date === dateStr && s.time === time)
 if (idx >= 0) rtPending.value.splice(idx, 1)
 else rtPending.value.push({ date: dateStr, time, duration_minutes: 30 })
}
const rtRemoveSlot = (s) => {
 rtPending.value = rtPending.value.filter(p => !(p.date === s.date && p.time === s.time))
 if (rtCustomSlot.value && rtCustomSlot.value.date === s.date && rtCustomSlot.value.time === s.time) {
 rtCustomSlot.value = null
 }
}

function rtAddCustomSlot() {
 if (!rtCustomDate.value || !rtCustomTime.value) return
 if (!rtIsPending(rtCustomDate.value, rtCustomTime.value)) {
 rtPending.value.push({ date: rtCustomDate.value, time: rtCustomTime.value, duration_minutes: 30 })
 }
 rtCustomSlot.value = { date: rtCustomDate.value, time: rtCustomTime.value }
 rtCustomTime.value = '15:00'
}

function toggleRequestTime() {
 if (rtOpen.value) { rtOpen.value = false; return }
 rtOpen.value = true
 fetchApi('/api/teacher-availability/list')
 .then(data => { rtTeachers.value = data.filter(t => !t.hidden) })
 .catch(() => {})
 rtWeekOff.value = 0
 rtPending.value = []
 rtCustomDate.value = _rtFmtDate(new Date())
 rtCustomTime.value = '15:00'
 rtCustomSlot.value = null
}

async function rtSendSlots(inquiry) {
 if (!rtTeacher.value || !rtPending.value.length) return
 if (actionPending.value) return
 actionPending.value = true
 try {
 const updated = await fetchApi(`/api/admin/submissions/${inquiry.id}/suggest-slots`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ teacher_name: rtTeacher.value.name, slots: rtPending.value }),
 })
 emit('updated', updated)
 rtPending.value = []
 rtOpen.value = false
 } catch (e) {
 console.error('Failed to send slots', e)
 } finally {
 actionPending.value = false
 }
}

// ── Note prompt: manual logs + the Follow Up snooze ─────────────────────────
// kind: 'call' | 'text' | 'email' | 'note' — one contact_interactions row on the
// family's contact (logInteraction), shown in every one of their signups' History.
// 'lost' is the optional reason note right after that action (also
// a plain note row). 'follow_up' snoozes the card instead of logging anything.
// `outcome` is only set for 'call'; `checkinStage` means a call/text here also
// resolves the check-in stage (the modal asks how it went) in the same Save.
const notePrompt = ref(null) // { kind, inquiry, outcome?, checkinStage? }
const notePromptSaving = ref(false)

const _QUICK_LOG_KINDS = new Set(['call', 'text', 'email'])
// Snoozes a card until a future date rather than logging a note — see
// scheduleFollowUp/saveNotePrompt below and inquiry_stage.py's _follow_up_state.
const _DATE_KINDS = new Set(['follow_up'])

const _NOTE_PROMPT_COPY = {
 lost:      { title: 'Lost Notes', subtitle: 'Why was this lost? e.g. went with another school, no response.' },
 note:      { title: 'Add Note', subtitle: 'What happened? e.g. parent called with a scheduling question.' },
 text:      { title: 'Log External Text', subtitle: 'A text sent from a personal phone. Optional — what was it about?' },
 email:     { title: 'Log External Email', subtitle: 'An email sent from a personal mailbox. Optional — what was it about?' },
 follow_up: { title: 'Schedule Follow-Up', subtitle: 'Card is hidden until this date, then reappears as a red Follow Up needing action. Note is optional.' },
 checkin:   { title: 'Check In' },
 reopen:    { title: 'Reopen Signup', subtitle: 'Where should this signup pick up? Note is optional, e.g. why they came back.' },
}
const notePromptTitle = computed(() => {
 const p = notePrompt.value
 if (p?.kind === 'call') return p.outcome === 'voicemail' ? 'Log Call — Left Voicemail' : 'Log Call — Spoke'
 if (p?.kind === 'text' && p.checkinStage) return 'Log Check-in Text'
 return _NOTE_PROMPT_COPY[p?.kind]?.title || 'Notes'
})
const notePromptSubtitle = computed(() => {
 const p = notePrompt.value
 if (p?.checkinStage) return 'Pick how it went below — this resolves the check-in stage. Note is optional.'
 if (p?.kind === 'call') return 'Optional — who did you talk to, or any details worth keeping?'
 return _NOTE_PROMPT_COPY[p?.kind]?.subtitle || ''
})
const notePromptNoteRequired = computed(() => !_QUICK_LOG_KINDS.has(notePrompt.value?.kind) && !_DATE_KINDS.has(notePrompt.value?.kind) && !['checkin', 'reopen'].includes(notePrompt.value?.kind))
const notePromptShowTimestamp = computed(() => _QUICK_LOG_KINDS.has(notePrompt.value?.kind))
const notePromptSaveLabel = computed(() => {
 const kind = notePrompt.value?.kind
 if (_QUICK_LOG_KINDS.has(kind)) return 'Log'
 if (_DATE_KINDS.has(kind)) return 'Schedule'
 if (kind === 'reopen') return 'Reopen'
 return kind === 'checkin' ? 'Save' : 'Save Note'
})
// 'resume' restores what mark-lost cancelled, quietly — no email goes out.
const notePromptReopenModes = computed(() => {
 const p = notePrompt.value
 if (p?.kind !== 'reopen') return null
 if (p.inquiry.status !== 'lost') return [['resume', 'Back to check-in'], ['follow_up', 'Follow up on a date']]
 return [['resume', 'Back to where they were'], ['fresh', 'Start fresh — needs new times'], ['follow_up', 'Follow up on a date']]
})
const notePromptShowCheckinOutcome = computed(() => !!notePrompt.value?.checkinStage)
const notePromptShowDatePicker = computed(() => _DATE_KINDS.has(notePrompt.value?.kind))

const closeNotePrompt = () => { notePrompt.value = null }

// From LogTouchButtons: { kind: 'call'|'text'|'email'|'note', outcome? }.
const startLog = (inquiry, { kind, outcome }) => {
 closeDropdown()
 const checkinStage = (kind === 'call' || kind === 'text') && cardAction(inquiry) === 'checkin'
 notePrompt.value = { kind, inquiry, outcome, checkinStage }
}

// Follow Up snooze: unlike Waitlist/Lost (which fire immediately, then open
// the modal for an optional note), this needs the date *before* anything is
// written, so the modal opens first and saveNotePrompt below does the call.
const scheduleFollowUp = (inquiry) => {
 notePrompt.value = { kind: 'follow_up', inquiry }
}

// Check In: resolve the check-in stage without logging a call/text — for when
// the outcome came from somewhere else (teacher, an email reply, payment).
const startCheckin = (inquiry) => {
 closeDropdown()
 notePrompt.value = { kind: 'checkin', inquiry, checkinStage: true }
}

const saveNotePrompt = async ({ note, occurredAt, checkinOutcome, followUpDate, reopenMode }) => {
 if (!notePrompt.value) return
 const { kind, inquiry, outcome, checkinStage } = notePrompt.value
 const isQuickLog = _QUICK_LOG_KINDS.has(kind)
 const isDateKind = _DATE_KINDS.has(kind)
 if (!isQuickLog && !isDateKind && !['checkin', 'reopen'].includes(kind) && !note) return // "Skip" on a note-only prompt does nothing
 if (isDateKind && !followUpDate) return
 if (checkinStage && checkinOutcome === 'follow_up' && !followUpDate) return
 notePromptSaving.value = true
 try {
 if (kind === 'reopen') {
 emit('updated', await fetchApi(`/api/admin/submissions/${inquiry.id}/revert`, {
 method: 'PATCH',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ mode: reopenMode, note: note || null, follow_up_date: followUpDate || null }),
 }))
 } else if (kind === 'checkin') {
 emit('updated', await fetchApi(`/api/admin/submissions/${inquiry.id}/checkin`, {
 method: 'PATCH',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ status: checkinOutcome, follow_up_date: followUpDate || null, note: note || null }),
 }))
 } else if (isDateKind) {
 emit('updated', await fetchApi(`/api/admin/submissions/${inquiry.id}/schedule-follow-up`, {
 method: 'PATCH',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ date: followUpDate, note: note || null }),
 }))
 } else {
 const { inquiries } = await logInteraction(inquiry.contact_id, {
 kind: isQuickLog ? kind : 'note',
 note: note || null,
 occurred_at: occurredAt || null,
 inquiry_id: inquiry.id,
 ...(kind === 'call' ? { outcome } : {}),
 ...(checkinStage ? { checkin_outcome: checkinOutcome } : {}),
 ...(checkinStage && checkinOutcome === 'follow_up' ? { follow_up_date: followUpDate } : {}),
 })
 // This card plus any sibling signup a call just marked called
 for (const row of inquiries) emit('updated', row)
 }
 invalidateHistory()
 } catch (e) {
 console.error('Failed to save note', e)
 } finally {
 notePromptSaving.value = false
 notePrompt.value = null
 }
}

const sendInviteDirect = async (inquiry, teacherName, slot) => {
 if (actionPending.value) return
 actionPending.value = true
 try {
 const updated = await fetchApi(`/api/admin/submissions/${inquiry.id}/send-invite-direct`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ teacher_name: teacherName, slot }),
 })
 emit('updated', updated)
 rtPending.value = []
 rtOpen.value = false
 } catch (e) {
 console.error('Failed to send direct invite', e)
 } finally {
 actionPending.value = false
 }
}

const rtSendInviteDirect = (inquiry) => {
 if (!rtTeacher.value || rtPending.value.length !== 1) return
 sendInviteDirect(inquiry, rtTeacher.value.name, rtPending.value[0])
}

const sendInviteFromApproval = (inquiry) => {
 const slot = (inquiry.booking_data || []).find(s => !s._cleared && s.teacher && s.source !== 'admin')
 if (!slot) return
 sendInviteDirect(inquiry, slot.teacher, slot)
}


onMounted(() => {
 document.addEventListener('click', closeDropdown)
 if (!teacherPhonesLoaded) {
 teacherPhonesLoaded = true
 fetchApi('/api/admin/teachers/phones').then(raw => {
 const normalized = {}
 for (const [name, phone] of Object.entries(raw))
 normalized[normalizeName(name)] = phone
 teacherPhones.value = normalized
 }).catch(() => { teacherPhonesLoaded = false })
 }
})

onUnmounted(() => {
 if (copiedTimer) clearTimeout(copiedTimer)
 if (smsTimer) clearTimeout(smsTimer)
 if (revertTimer) clearTimeout(revertTimer)
 if (clearSlotTimer) clearTimeout(clearSlotTimer)
 document.removeEventListener('click', closeDropdown)
 if (actionPending.value) emit('busy', false)
})
</script>

<style scoped>
.actions-dropdown {
 @apply absolute right-0 bottom-full mb-1 z-20 bg-surface dark:bg-surface-dark border border-stroke-subtle dark:border-stroke-dark rounded-lg shadow-lg overflow-hidden min-w-[130px];
}
.dropdown-item {
 @apply block w-full text-left px-3 py-2 text-sm text-fg-secondary dark:text-fg-secondary-dark transition-colors disabled:opacity-40 focus:outline-none;
}
</style>
